// Tasks types for React Native admin app
// Based on admin-web tasks implementation

export interface ISubtaskItem {
  id: number;
  title: string;
  sortIndex: number;
  assignee: string;
  completedAt?: Date | string | null;
  createdAt?: string;
}

export interface ITaskFile {
  id: number;
  fileUrl: string;
  label: string;
  name?: string;
}

export interface ITaskAssignee {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export enum TaskPriorityEnum {
  Low = 1,
  Normal = 2,
  High = 3,
  Urgent = 4,
  Critical = 5,
}

export interface ITaskItemAttributes {
  id: number;
  title: string;
  taskStatus: TaskStatusType;
  priority: TaskPriorityEnum;
  isPrivate: boolean;
  isResidentRequest: boolean;
  propertyId: number;
  creatorId?: string;
  subtasks: ISubtaskItem[];
  assignees?: ITaskAssignee[];
  files?: ITaskFile[];
  location?: string;
  category?: string;
  subcategory?: string;
  isBlocked?: boolean;
  dueDate?: Date | string | null;
  description?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  completedAt?: Date | string | null;
  isArchived?: boolean;
  archivedAt?: Date | string | null;
}

export interface IArchivedTaskParams {
  month: number;
  year: number;
  propertyId: number;
  isArchived?: boolean;
  archivedAt?: Date | string;
}

export const TASK_PRIORITY = [
  {
    value: 1,
    label: "Low",
    color: "gray",
    pillColor: "bg-white/5 text-gray-400 ring-gray-400",
  },
  {
    value: 2,
    label: "Normal",
    color: "green",
    pillColor: "bg-green-500/20 text-green-500 ring-green-400",
  },
  {
    value: 3,
    label: "High",
    color: "yellow",
    pillColor: "bg-yellow-500/20 text-yellow-500 ring-yellow-400",
  },
  {
    value: 4,
    label: "Urgent",
    color: "red",
    pillColor: "bg-red-500/20 text-red-500 ring-red-400",
  },
  {
    value: 5,
    label: "🔥 Critical",
    color: "brightRed",
    pillColor: "bg-red-900/50 text-red-400 ring-red-300",
  },
];

export const TASK_STATUS_CONFIG = {
  incoming: {
    value: "incoming",
    label: "Incoming",
    color: "gray",
  },
  pending: {
    value: "pending",
    label: "Backlog",
    color: "gray",
  },
  todo: {
    value: "todo",
    label: "To Do",
    color: "blue",
  },
  inProgress: {
    value: "inProgress",
    label: "In Progress",
    color: "yellow",
  },
  completed: {
    value: "completed",
    label: "Completed",
    color: "green",
  },
  cancelled: {
    value: "cancelled",
    label: "Cancelled",
    color: "red",
  },
} as const;

export type TaskStatusType = keyof typeof TASK_STATUS_CONFIG;

export const TASK_CATEGORY_CONFIG = {
  maintenance: {
    key: "maintenance",
    label: "Maintenance",
  },
  it: {
    key: "it",
    label: "IT",
  },
} as const;

export type TaskCategoryType = keyof typeof TASK_CATEGORY_CONFIG;

// Task list response interface
export interface ITaskListResponse {
  data: ITaskItemAttributes[];
  count: number;
  hasMore?: boolean;
}

// Task table row interface for display
export interface ITaskTableRow {
  id: number;
  title: string;
  taskStatus: TaskStatusType;
  priority: TaskPriorityEnum;
  category: string;
  assignees?: ITaskAssignee[];
  dueDate?: Date | string | null;
  isBlocked?: boolean;
  subtasks: ISubtaskItem[];
  files?: ITaskFile[];
  isArchived?: boolean;
  createdAt?: Date | string;
  description?: string;
  location?: string;
}

// Filter model for tasks
export interface ITaskFilterModel {
  search: string;
  creator: string[];
  assignedTo: string[] | undefined;
  month?: number;
  year?: number;
  status?: TaskStatusType[];
  // Exclusion filters (for 'is not' operator)
  creatorNot?: string[];
  assignedToNot?: string[];
  statusNot?: TaskStatusType[];
}

// Sort model for tasks
export interface ITaskSortModel {
  field: 'newestCreationDate' | 'oldestCreationDate' | 'soonestDueDate' | 'latestDueDate' | 'highestPriority' | 'lowestPriority' | 'newestArchiveDate';
  sort: 'asc' | 'desc';
}

export const TASK_SORT_OPTIONS = [
  {
    value: "newestArchiveDate",
    label: "Newest Archive Date",
    keys: ["archivedAt", "priority"],
    order: ["desc", "asc"],
  },
  {
    value: "newestCreationDate",
    label: "Newest Creation Date",
    keys: ["createdAt", "priority"],
    order: ["desc", "asc"],
  },
  {
    value: "oldestCreationDate",
    label: "Oldest Creation Date",
    keys: ["createdAt", "priority"],
    order: ["asc", "asc"],
  },
  {
    value: "soonestDueDate",
    label: "Soonest Due Date",
    keys: ["dueDate", "priority"],
    order: ["asc", "asc"],
  },
  {
    value: "latestDueDate",
    label: "Latest Due Date",
    keys: ["dueDate", "priority"],
    order: ["desc", "asc"],
  },
  {
    value: "highestPriority",
    label: "Highest Priority",
    keys: ["priority", "createdAt"],
    order: ["desc", "desc"],
  },
  {
    value: "lowestPriority",
    label: "Lowest Priority",
    keys: ["priority", "createdAt"],
    order: ["asc", "desc"],
  },
] as const;

// Form values for creating/editing tasks
export interface ITaskFormValues {
  title: string;
  description?: string;
  taskStatus: TaskStatusType;
  priority: TaskPriorityEnum;
  category: TaskCategoryType;
  location?: string;
  dueDate?: Date | string | null;
  assignees: ITaskAssignee[];
  subtasks: ISubtaskItem[];
  files?: ITaskFile[];
  isBlocked?: boolean;
  isPrivate?: boolean;
}

// Default form values
export const defaultTaskFormValues: ITaskFormValues = {
  title: '',
  description: '',
  taskStatus: 'todo',
  priority: TaskPriorityEnum.Normal,
  category: 'maintenance',
  location: '',
  dueDate: null,
  assignees: [],
  subtasks: [],
  files: [],
  isBlocked: false,
  isPrivate: false,
};

// Creator filter options
export const CREATOR_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "resident", label: "Resident" },
] as const;

// Task view types
export const TASK_VIEW_TYPES = {
  LIST: 'list',
  ARCHIVED: 'archived',
} as const;

export type TaskViewType = typeof TASK_VIEW_TYPES[keyof typeof TASK_VIEW_TYPES];

// Quick create task interface
export interface IQuickCreateTask {
  title: string;
  taskStatus: TaskStatusType;
  propertyId: number;
}

// Task duplication interface
export interface ITaskDuplicateRequest {
  taskId: number;
}

// Task archive/unarchive interface
export interface ITaskArchiveRequest {
  taskId: number;
  isArchived: boolean;
  archivedAt?: string | null;
}

// Available assignees interface
export interface IAvailableAssignee {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

// Table column keys for tasks
export enum TaskTableKeys {
  TITLE = 'title',
  STATUS = 'taskStatus',
  PRIORITY = 'priority',
  ASSIGNEE = 'assignees',
  DUE_DATE = 'dueDate',
  CATEGORY = 'category',
  CREATED_AT = 'createdAt',
}
